/* -------------------------------------------------------------------------- */
/* 1. Les registres                                                           */
/* -------------------------------------------------------------------------- */
/** Qui a posé un objectif — `ObjectifChapitre.definiParRole`. */
export const ROLES_AUTEUR_OBJECTIF = ['eleve', 'tuteur', 'parent'];
/** La nature d'une évaluation de classe — `EvaluationClasse.nature`. */
export const NATURES_EVALUATION_CLASSE = [
    'mini_test',
    'examen',
    'situation_probleme',
    'epreuve_ministerielle',
];
/**
 * UN SEUL jeu de libellés, élève et tuteur (`DECISIONS-ARCHITECTE.md`,
 * point 14). Le mot « test » n'est jamais dit : `mini_test` est une valeur
 * interne.
 */
export const LIBELLES_NATURE_EVALUATION = {
    mini_test: 'Courte évaluation',
    examen: 'Évaluation',
    situation_probleme: 'Situation-problème',
    epreuve_ministerielle: 'Épreuve ministérielle',
};
/** La fenêtre de révision avant une évaluation, en jours, selon sa nature (MV-MAI-07). */
export const FENETRE_REVISION_JOURS = {
    mini_test: 3,
    examen: 7,
    situation_probleme: 7,
    epreuve_ministerielle: 7,
};
/** Les quatre ÉTATS de préparation — jamais un nombre (MV-MAI-08, MV-UX-03). */
export const ETATS_PREPARATION = ['pret', 'en_bonne_voie', 'a_renforcer', 'a_commencer'];
/** Un seul jeu de mots, élève, parent et tuteur (`DECISIONS-ARCHITECTE.md`, point 14). */
export const LIBELLES_ETAT_PREPARATION = {
    pret: 'Prêt',
    en_bonne_voie: 'En bonne voie',
    a_renforcer: 'À renforcer',
    a_commencer: 'À commencer',
};
/** Où en est la classe dans le chapitre, d'après les dates de l'objectif. */
export const PHASES_CALENDRIER = ['avant', 'en_cours', 'revision', 'termine'];
/** Une case de la frise du chapitre. */
export const ETATS_CASE_FRISE = ['vue', 'ici', 'a_venir'];
/** Ce que le prochain geste — ou un pas de plan — désigne. */
export const GENRES_DE_GESTE = ['exercice', 'fiche', 'capsule'];
export const LIBELLES_GENRE_DE_GESTE = {
    exercice: 'Exercice',
    fiche: 'Fiche',
    capsule: 'Capsule',
};
/**
 * Qui parle dans le prochain geste, DANS L'ORDRE DE PRIORITÉ (MV-MAI-10) : la
 * liste du tuteur d'abord, puis la notion, puis le moteur. `tuteur` : le
 * premier élément non fait de la séquence la plus urgente (Φ-MV-5,
 * `manuel-tuteur.ts` § 4).
 *
 * `evaluation` — le pas du plan d'une évaluation à sept jours ou moins — n'est
 * plus rendue depuis le retrait du plan (fondateur, 20 oct. 2026, AB#977).
 * Elle reste au registre : les traces passées la portent.
 *
 * `notion` (chantier « Élève », lot 2, AB#937 — la règle de l'élément servi) :
 * la capsule de la notion du moment que l'élève n'a jamais vue jusqu'au bout,
 * à défaut celle de la notion suivante. Ajoutée À LA FIN : l'ordre du tableau
 * n'est pas celui de la priorité, et aucune valeur existante ne bouge.
 *
 * `parcours` (lot P1, AB#973) : l'étape suivante du parcours EN COURS le plus
 * ancien de la matière, quand l'élève (ou le moteur) l'a écrit. Celui d'un
 * tuteur garde la voix `tuteur`. Avec elle, c'est la priorité n° 1. Ajoutée À
 * LA FIN, pour la même raison.
 */
export const VOIX_DU_GESTE = ['tuteur', 'evaluation', 'moteur', 'notion', 'parcours'];
/** Pourquoi le moteur propose CET exercice (MV-MAI-09). */
export const NATURES_PROPOSITION = [
    'notion_du_moment',
    'monter_de_niveau',
    'retour_espace',
    'prerequis',
    'sequence_tuteur',
    'preparation',
];
export const VERBES_DU_GESTE = ['Commencer', 'Continuer'];
/** Bornes de ce qu'un objectif accepte. */
export const MAX_EVALUATIONS_OBJECTIF = 12;
export const MAX_CARACTERES_TITRE_EVALUATION = 80;
/** Durée proposée d'un chapitre, en semaines, et celles que l'écran offre. */
export const SEMAINES_DE_CHAPITRE_PAR_DEFAUT = 4;
export const SEMAINES_DE_CHAPITRE_MAX = 12;
/** Une date civile, `AAAA-MM-JJ`. */
export const MOTIF_DATE_CIVILE = /^\d{4}-\d{2}-\d{2}$/;
/** `valeur` est-elle une date civile RÉELLE (le 30 février n'en est pas une) ? */
export function estDateCivile(valeur) {
    if (typeof valeur !== 'string' || !MOTIF_DATE_CIVILE.test(valeur))
        return false;
    const d = new Date(`${valeur}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === valeur;
}
/** Les filtres de l'historique (MV-ELV-08). */
export const FILTRES_HISTORIQUE = ['tout', 'exercices', 'devoirs'];
/** Ce qu'une trace de l'historique est : un essai d'exercice, un défi, un devoir photographié. */
export const GENRES_DE_TRACE = ['exercice', 'defi', 'devoir'];
/** Le mot du verdict dans l'historique. Jamais « faux ». */
export const LIBELLES_VERDICT_HISTORIQUE = {
    juste: 'Réussi',
    partiel: 'Presque',
    erreur: 'À revoir',
};
/** Ce que la correction rend quand une démarche n'a pas pu être lue ET que le calcul ne peut rien en dire. */
export const TITRE_EN_ATTENTE_DE_LECTURE = 'En attente de lecture.';
export const MESSAGE_EN_ATTENTE_DE_LECTURE = 'On n’a pas réussi à lire ta démarche. Rien n’est compté : réessaie dans un instant.';
/** Le même repli pour une réponse RÉDIGÉE : il n'y a pas de « démarche » à lire. */
export const MESSAGE_EN_ATTENTE_DE_LECTURE_TEXTE = 'On n’a pas réussi à lire ta réponse. Rien n’est compté : réessaie dans un instant.';
/* -------------------------------------------------------------------------- */
/* 8. La position face au programme — /v1/tuteur/manuel (F-15)                */
/* -------------------------------------------------------------------------- */
export const ETATS_ATTENTE = ['atteinte', 'en_chemin'];
/**
 * Ce que la pastille d'une attente DIT (`UX-7-35`, Φ-MV-7). L'état reste à deux
 * valeurs ; le libellé en distingue trois : une attente « en chemin » SANS
 * AUCUN travail n'est pas « en route », elle n'est pas commencée.
 */
export const LIBELLES_ETAT_ATTENTE = {
    atteinte: 'Atteinte',
    en_route: 'En route',
    pas_commencee: 'Pas commencée',
};
/** Le libellé de l'état d'une attente, d'après son état et le nombre de travaux qui la mesurent. */
export function libelleEtatAttente(etat, travaux) {
    if (etat === 'atteinte')
        return LIBELLES_ETAT_ATTENTE.atteinte;
    return travaux > 0 ? LIBELLES_ETAT_ATTENTE.en_route : LIBELLES_ETAT_ATTENTE.pas_commencee;
}
/** Sous ce seuil de confiance, la ligne est rendue « à confirmer ». */
export const CONFIANCE_LIGNE_A_CONFIRMER = 0.8;
/** Une image de ligne : bien moins qu'une page. */
export const MAX_OCTETS_BASE64_LIGNE = 4_000_000;
//# sourceMappingURL=manuel-objectif.js.map